import { useState, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import useApi from '../../Api';
import useUserVerification from '../../components/user/useUserVerification';
import { colors } from '../../components/common/ColorPicker';

const useCreateItinerary = () => {
  const apiClient = useApi();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { verifyUser, modals, setShowLoginModal, setShowResendModal } = useUserVerification();

  const [isCreating, setIsCreating] = useState(false);

  const createItinerary = useCallback(
    async ({ originId = 0, originName = 'General', editable = true }) => {
      const status = verifyUser({ login: true, verified: true, premium: true });

      if (status === 'login') return setShowLoginModal(true);
      if (status === 'verified') return setShowResendModal(true);
      if (status === 'premium') return;

      if (isCreating) return;
      setIsCreating(true);

      try {
        const itinerary = {
          id: uuidv4(),
          name: `${originName} Trip`,
          origin: originName,
          origin_id: originId,
          columns: [],
          columnVisibility: { 'saved-places': true },
          columnMarkerColors: { 'saved-places': colors[0] },
          isPublic: false,
          tags: []
        };

        await apiClient.post('/itinerary/new', itinerary, {
          headers: { Authorization: `Bearer ${user.token}` }
        });

        const path = editable
          ? `/itinerary/${encodeURIComponent(originId)}/edit/${encodeURIComponent(itinerary.id)}`
          : `/itinerary/${encodeURIComponent(originId)}/${encodeURIComponent(itinerary.id)}`;

        navigate(path, { state: { originName } });
      } catch {
        toast.error('Failed to create itinerary. Please try again.', {
          position: 'top-center',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
      } finally {
        setIsCreating(false);
      }
    },
    [apiClient, user, verifyUser, navigate, isCreating]
  );

  return { createItinerary, isCreating, modals };
};

export default useCreateItinerary;
